import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LeapV2Ilp } from '../models/leap-v2-ilp.model';
import { LeapV2UploadedFile } from '../models/leap-v2-student.model';
import { EvidenceFileDto, IlpDto, IlpUnlockRequest } from '../models/leap-v2-api.model';
import { BaseService } from '../../../../_services/base/base.service';
import { SessionUserHashManagerService } from '../../../../_services/session-user-hash-manager/session-user-hash-manager.service';
import { environment } from '../../../../../environments/environment';
import { buildEvidenceDtos } from './leap-v2-progress-report.service';
import { clampSemh, SEMH_MIN } from '../models/leap-v2-semh';
import { ilpStatusLabel } from '../models/leap-v2-ilp-status';
import { KeyStage } from '../../../../_models/tutor-reports/tutorreport';
import { toIsoDate } from '../../../../shared/filter-ux/date-presets';

/**
 * Data access for the LEAP v2 Individual Learning Plan.
 *
 * Targets the Tutor Portal API Ilp controller. Follows the shared BaseService
 * pattern (Bearer + RequestId + RequestMatch headers via prepareOptions;
 * status===299 / HttpErrorResponse guard; handleError).
 */
@Injectable({ providedIn: 'root' })
export class LeapV2IlpService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient,
    protected hashManagerService: SessionUserHashManagerService
  ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  /**
   * Loads the learner's ILP (with evidence URLs). loadingPage: the page is loading the booking in its URL, so not
   * found reads as one plain message (40973).
   */
  loadIlp(placementId: number, studentId: number, loadingPage: boolean = false): Observable<LeapV2Ilp> {
    // GET sends no body, so the request hash must cover the empty-body
    // placeholder (prepareOptions(null)).
    const options = loadingPage ? this.prepareBookingLoadOptions(null) : this.prepareOptions(null);
    return this.http.get(this.apiUrl + 'api/Ilp/' + placementId + '/' + studentId, options).pipe(
      map((data: any) => this.unwrap(data) as IlpDto),
      map(dto => dto ? this.fromDto(dto) : this.emptyIlp(placementId, studentId)),
      catchError(loadingPage ? this.handleBookingLoadError('loadIlp') : this.handleError('loadIlp'))
    );
  }

  /** The key stage options (dbo.tblKeyStage), read from the ILP frame. loadingPage: as loadIlp (40973). */
  loadKeyStages(placementId: number, studentId: number, loadingPage: boolean = false): Observable<KeyStage[]> {
    const body = { placementId, studentId, isStudentGroup: false };
    const options = loadingPage ? this.prepareBookingLoadOptions(body) : this.prepareOptions(body);
    return this.http.post(this.apiUrl + 'api/TutorReport/GetIndividualLearningPlanFrame/', body, options).pipe(
      map((data: any) => (this.unwrap(data)?.keyStages ?? []) as KeyStage[]),
      catchError(loadingPage ? this.handleBookingLoadError('loadKeyStages') : this.handleError('loadKeyStages'))
    );
  }

  /** Persists the ILP as a draft. Resolves to the saved row id. */
  saveIlp(ilp: LeapV2Ilp): Observable<number> {
    return from(this.toDto(ilp)).pipe(
      switchMap(dto => this.http.post(this.apiUrl + 'api/Ilp/save', dto, this.prepareOptions(dto))),
      map((data: any) => this.unwrap(data) as number),
      catchError(this.handleError('saveIlp'))
    );
  }

  /** Submits the ILP for approval. Resolves to the saved row id. */
  submitIlp(ilp: LeapV2Ilp): Observable<number> {
    return from(this.toDto(ilp)).pipe(
      switchMap(dto => this.http.post(this.apiUrl + 'api/Ilp/submit', dto, this.prepareOptions(dto))),
      map((data: any) => this.unwrap(data) as number),
      catchError(this.handleError('submitIlp'))
    );
  }

  /** Unlocks an approved ILP back to draft. */
  unlockIlp(placementId: number, studentId: number): Observable<boolean> {
    const body: IlpUnlockRequest = { placementId, studentId };
    return this.http.post(this.apiUrl + 'api/Ilp/unlock', body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as boolean),
      catchError(this.handleError('unlockIlp'))
    );
  }

  /** QA sign-off: Submitted → Approved. Keyed by tblBaseline RowId, not placement+student. */
  authoriseIlp(rowId: number): Observable<boolean> {
    const body = { rowId: rowId };
    return this.http.post(this.apiUrl + 'api/Ilp/authorise', body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as boolean),
      catchError(this.handleError('authoriseIlp'))
    );
  }

  /** QA rejection: Submitted → Rejected. The reason is 20–200 characters and goes back to the tutor. */
  rejectIlp(rowId: number, reason: string): Observable<boolean> {
    const body = { rowId: rowId, reason: reason };
    return this.http.post(this.apiUrl + 'api/Ilp/reject', body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as boolean),
      catchError(this.handleError('rejectIlp'))
    );
  }

  // --- mapping ---------------------------------------------------------------

  private fromDto(dto: IlpDto): LeapV2Ilp {
    return {
      rowId: dto.rowId,
      placementId: dto.placementId,
      studentId: dto.studentId,
      statusId: dto.statusId,
      date: dto.baselineDate ? new Date(dto.baselineDate) : new Date(),
      approved: dto.approved,
      // The API sends StatusId but no StatusDescription, so the label comes from the id.
      status: dto.statusDescription ?? ilpStatusLabel(dto.statusId, dto.approved),
      // Key stage is a tblKeyStage Id (null = not chosen). SEMH ratings are 1..5 — see leap-v2-semh.ts.
      academic: {
        initialKeyStage: dto.initialKeyStage ?? null,
        initialScorePct: dto.initialAssessmentScore ?? 0,
        targetScorePct: dto.targetAssessmentScore ?? 0,
        assessmentUsed: dto.assessmentUsed ?? '',
      },
      // Ratings are 1..5 on the wire; clampSemh also lifts legacy 0-based rows (see leap-v2-semh.ts).
      semh: {
        confidence: clampSemh(dto.confidenceRating),
        resilience: clampSemh(dto.resilienceRating),
        independence: clampSemh(dto.independenceRating),
        motivation: clampSemh(dto.motivationRating),
        selfRegulation: clampSemh(dto.selfRegulationRating),
      },
      areasOfStrength: dto.areasOfStrength ?? '',
      areasForDevelopment: dto.areasForDevelopment ?? '',
      planForProgress: dto.plan ?? '',
      evidence: (dto.evidence ?? []).map(evidenceFromDto),
    };
  }

  private async toDto(ilp: LeapV2Ilp): Promise<IlpDto> {
    return {
      rowId: ilp.rowId ?? 0,
      placementId: ilp.placementId ?? 0,
      studentId: ilp.studentId ?? 0,
      statusId: ilp.statusId,
      // The plain local date: toISOString() turns local midnight in BST into the previous day in UTC,
      // and the API keeps only the date, so the plan's date moved back a day on every save.
      baselineDate: toIsoDate(ilp.date ? new Date(ilp.date) : new Date()),
      approved: ilp.approved,
      submitted: ilp.status === 'Approved' || ilp.status === 'Submitted',
      statusDescription: ilp.status,
      initialKeyStage: ilp.academic.initialKeyStage ?? null,
      initialAssessmentScore: ilp.academic.initialScorePct,
      targetAssessmentScore: ilp.academic.targetScorePct,
      assessmentUsed: ilp.academic.assessmentUsed,
      confidenceRating: ilp.semh.confidence,
      resilienceRating: ilp.semh.resilience,
      independenceRating: ilp.semh.independence,
      motivationRating: ilp.semh.motivation,
      selfRegulationRating: ilp.semh.selfRegulation,
      areasOfStrength: ilp.areasOfStrength,
      areasForDevelopment: ilp.areasForDevelopment,
      plan: ilp.planForProgress,
      evidence: await buildEvidenceDtos(ilp.evidence),
      // The list is every document the screen shows, so the API may remove a stored one missing from it (40454).
      evidenceIsComplete: true,
    };
  }

  private emptyIlp(placementId: number, studentId: number): LeapV2Ilp {
    return {
      rowId: 0,
      placementId,
      studentId,
      date: new Date(),
      approved: false,
      status: 'Draft',
      academic: { initialKeyStage: null, initialScorePct: 0, targetScorePct: 0, assessmentUsed: '' },
      semh: { confidence: SEMH_MIN, resilience: SEMH_MIN, independence: SEMH_MIN, motivation: SEMH_MIN, selfRegulation: SEMH_MIN },
      areasOfStrength: '',
      areasForDevelopment: '',
      planForProgress: '',
      evidence: [],
    };
  }

  /**
   * Shared body unwrap used by every method — 422 business-validation failures arrive on the
   * error channel and are mapped by BaseService.handleError.
   */
  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}

/** Maps a loaded evidence DTO to the upload view-model (no in-memory File). */
function evidenceFromDto(dto: EvidenceFileDto): LeapV2UploadedFile {
  return { name: dto.name, size: dto.size, contentType: dto.contentType, url: dto.url };
}
